import { Dispatch, useMemo, useState } from 'react'
import { Moment } from 'moment'
import {
  Card,
  Text,
  Button,
  Icon,
  CardColors,
  Separator,
  Tooltip,
  Checkbox,
} from '@/ui'
import { DeleteStatement } from './DeleteStatementModal'
import { UploadDocumentType } from '@/constants/businessConstants'
import { UserDocument } from '@/features/user/your-practice/documents/lib/userDocuments.slice'
import { regular, solid } from '@fortawesome/fontawesome-svg-core/import.macro'

// TODO: Migrate to V2
import FileUploadModal from '@/componentsV1/FileUpload/FileUploadModal'
import { TypographyStyles } from '@/styles'
import { FEATURE_FLAG_KEYS } from '@/features/open-feature'
import { useBooleanFlagValue } from '@openfeature/react-sdk'
import { NotExpectedStateAction } from '@/features/onboarding/upload-statements-stage/types'

export const MissingStatementCard = ({
  month,
  yearMonth,
  financialAccountId,
  statements,
  notExpected,
  setNotExpectedValue,
}: {
  month: string
  yearMonth: string
  financialAccountId: number
  statements: UserDocument[]
  notExpected: boolean
  setNotExpectedValue: Dispatch<NotExpectedStateAction>
}) => {
  const [uploadedStatements, setUploadedStatements] = useState<UserDocument[]>(
    []
  )
  const [openUpload, setOpenUpload] = useState(false)
  const [deleteDocumentModalOpen, setDeleteDocumentModalOpen] = useState(false)
  const [documentToDelete, setDocumentToDelete] = useState<UserDocument>()

  const shouldEnableNoStatementCheckbox = useBooleanFlagValue(
    FEATURE_FLAG_KEYS.enableNoStatementCheckbox,
    false
  )

  const setUploadedFile = ({
    doc,
  }: {
    statementMonth?: Moment
    fileName?: string
    id?: number
    doc?: UserDocument
  }) => {
    if (doc?.id && doc.statementMonth === yearMonth) {
      const statements = [...uploadedStatements, doc]
      setUploadedStatements(statements)
    }
  }

  const filteredStatements = useMemo(() => {
    return statements.filter(
      (statement) =>
        statement.statementMonth === yearMonth &&
        statement.statement?.statementFinancialAccounts?.some(
          (account) => account.financialAccountId === financialAccountId
        )
    )
  }, [financialAccountId, statements, yearMonth])

  const isCompleted =
    uploadedStatements.length >= 1 || Boolean(filteredStatements.length)

  const { cardColor, iconProps } = useMemo(() => {
    if (notExpected) {
      return {
        cardColor: CardColors.LEVEL_2,
        iconProps: {
          icon: solid('xmark'),
          color: 'text-neutral-300',
        },
      }
    }

    if (isCompleted) {
      return {
        cardColor: CardColors.SUCCESS,
        iconProps: {
          icon: solid('check'),
          color: 'text-green-500',
        },
      }
    }

    return {
      cardColor: CardColors.PRIORITY,
      iconProps: {
        icon: regular('circle'),
        color: 'text-neutral-500',
      },
    }
  }, [isCompleted, notExpected])

  return (
    <Card color={cardColor}>
      <div className="flex flex-row justify-between items-center w-full">
        <div className="flex flex-col items-start flex-grow mr-20">
          <div className="flex flex-row items-center w-full">
            <Icon backgroundStyle="circle" size="2x" {...iconProps} />
            <Text variant={TypographyStyles.BODY_MD}>{month}</Text>
          </div>

          {isCompleted && (
            <div className="flex flex-col items-start justify-between pt-2 text-center ml-16 w-full">
              <Separator />
              {filteredStatements.map((file: UserDocument) => (
                <Text key={file.id}>
                  {file.fileDescription}
                  <Button
                    onClick={() => {
                      setDeleteDocumentModalOpen(true)
                      setDocumentToDelete(file)
                    }}
                    iconLeft={regular('trash-can')}
                    variant="discreet"
                    className="ml-2"
                  />
                </Text>
              ))}
            </div>
          )}
        </div>

        <div className="flex flex-col justify-end items-end mt-5">
          <div>
            <Button
              onClick={() => {
                setOpenUpload(true)
              }}
              variant="secondary"
              className="flex items-center w-[150px]"
            >
              <Icon icon={regular('upload')} size="1x" className="mr-1" />
              Upload
            </Button>
          </div>
          {shouldEnableNoStatementCheckbox && (
            <div className="flex flex-row gap-2 mt-2">
              <Checkbox
                onCheckedChange={(checked) => {
                  setNotExpectedValue({
                    value: checked,
                    monthYear: yearMonth,
                    financialAccountId,
                  })
                }}
                checked={notExpected}
              />
              <Tooltip>
                <Tooltip.Trigger>
                  <Text
                    className="border-b-2 border-green-50 border-dotted hover:cursor-pointer hover:text-brand active:text-neutral"
                    variant={TypographyStyles.BODY_SM}
                  >
                    This account wasn’t open yet
                  </Text>
                </Tooltip.Trigger>
                <Tooltip.Content header="This account wasn’t open yet">
                  <Text variant={TypographyStyles.BODY_SM}>
                    Select this if you hadn’t opened the account yet and
                    therefore don’t have a statement for this month.
                  </Text>
                </Tooltip.Content>
              </Tooltip>
            </div>
          )}
        </div>
      </div>

      <FileUploadModal
        documentType={UploadDocumentType.STATEMENT}
        open={openUpload}
        close={() => setOpenUpload(false)}
        userFacing
        monthYear={yearMonth || undefined}
        financialAccountId={[financialAccountId]}
        title="Upload Statement"
        description="Upload statement for the selected account and month"
        subHeaderText=" "
        setUploadedFile={setUploadedFile}
      />

      <DeleteStatement
        document={documentToDelete}
        open={deleteDocumentModalOpen}
        close={() => setDeleteDocumentModalOpen(false)}
        updateDocumentStatements={() =>
          setUploadedStatements((docs: UserDocument[]) =>
            docs.filter((doc) => doc.id !== documentToDelete?.id)
          )
        }
      />
    </Card>
  )
}
